// Tastaturbedienung der Projekttabelle (UX-Vorschlag P6, Flag `ux_web_p6`).
//
//   j / ↓   nächste Zeile        k / ↑   vorige Zeile
//   x       Zeile markieren      ↵       Aufgabe öffnen
//   e       umbenennen           s / p   Status / Priorität weiterschalten
//   a       Sub-Aufgabe anlegen (die Zeilen-Schaltfläche ist nur bei
//           Hover/Fokus sichtbar — so bleibt sie per Tastatur erreichbar)
//
// Reine Logik (testbar) + ein dünner DOM-Hook. Zeilen sind Elemente mit
// `data-task-row="<id>"` innerhalb des Containers; der Fokus liegt echt (DOM)
// auf der Zeile, damit Screenreader und :focus-visible mitspielen.
//
// Nie auslösen: in Eingabefeldern, mit ⌘/Strg/Alt, wenn ein Dialog offen ist,
// und direkt nach `g` (sonst schaltete „g p" = Projekte zugleich die Priorität).

import { useEffect, useRef } from "preact/hooks";
import { TASK_PRIORITIES, type TaskPriority } from "@juuplan/shared";
import { isEditableTarget } from "./hotkeys";

export type RowAction =
  | { type: "move"; dir: 1 | -1 }
  | { type: "select" }
  | { type: "open" }
  | { type: "rename" }
  | { type: "status" }
  | { type: "priority" }
  | { type: "subtask" };

/**
 * Taste → Aktion. `rowFocused` = der Fokus liegt auf einer Tabellenzeile.
 * Pfeiltasten wirken nur mit Zeilenfokus (sonst scrollen sie die Seite wie
 * bisher); j/k holen den Fokus auch ohne in die erste Zeile.
 */
export function rowActionForKey(key: string, rowFocused: boolean): RowAction | null {
  switch (key) {
    case "j":
      return { type: "move", dir: 1 };
    case "k":
      return { type: "move", dir: -1 };
    case "ArrowDown":
      return rowFocused ? { type: "move", dir: 1 } : null;
    case "ArrowUp":
      return rowFocused ? { type: "move", dir: -1 } : null;
  }
  if (!rowFocused) return null;
  switch (key) {
    case "x":
      return { type: "select" };
    case "Enter":
      return { type: "open" };
    case "e":
      return { type: "rename" };
    case "s":
      return { type: "status" };
    case "p":
      return { type: "priority" };
    case "a":
      return { type: "subtask" };
  }
  return null;
}

/** Ziel-Index beim Bewegen; ohne aktuelle Zeile startet ↓ oben, ↑ unten. */
export function moveIndex(current: number, count: number, dir: 1 | -1): number {
  if (count === 0) return -1;
  if (current < 0) return dir === 1 ? 0 : count - 1;
  return Math.max(0, Math.min(count - 1, current + dir));
}

/** Nächster Status in Projektreihenfolge (zyklisch). */
export function nextStatusId(statuses: Array<{ id: number }>, currentId: number | null | undefined): number | null {
  if (statuses.length === 0) return null;
  const i = statuses.findIndex((s) => s.id === currentId);
  return statuses[(i + 1) % statuses.length]!.id;
}

/** Priorität: keine → niedrig → mittel → hoch → dringend → keine. */
export function nextPriority(current: TaskPriority | null | undefined): TaskPriority | null {
  if (!current) return TASK_PRIORITIES[0];
  const i = TASK_PRIORITIES.indexOf(current);
  return i < 0 || i === TASK_PRIORITIES.length - 1 ? null : TASK_PRIORITIES[i + 1]!;
}

export interface TableKeyHandlers {
  onSelect: (id: number) => void;
  onOpen: (id: number) => void;
  onRename: (id: number) => void;
  onStatus: (id: number) => void;
  onPriority: (id: number) => void;
  onSubtask: (id: number) => void;
}

/** Sichtbare Zeilen in Dokumentreihenfolge (eingeklappte/gefilterte zählen nicht). */
export function visibleRows(container: Element): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>("[data-task-row]")).filter(
    (el) => !el.closest('[style*="display: none"], [hidden]')
  );
}

function rowOf(el: Element | null): HTMLElement | null {
  return (el?.closest?.("[data-task-row]") as HTMLElement | null) ?? null;
}

/**
 * Bindet die Tasten an `window`, solange `enabled`. Wirkt nur, wenn der Fokus
 * im Container oder auf dem Dokument liegt (nicht in fremden Panels).
 */
export function useTableKeys(
  containerRef: { current: HTMLElement | null },
  enabled: boolean,
  handlers: TableKeyHandlers
): void {
  const h = useRef(handlers);
  h.current = handlers;

  useEffect(() => {
    if (!enabled) return;
    let lastG = 0;
    const onKeyDown = (e: KeyboardEvent): void => {
      const container = containerRef.current;
      if (!container) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as Element | null;
      if (isEditableTarget(target)) return;
      if (e.key === "g") {
        lastG = Date.now();
        return;
      }
      if (Date.now() - lastG < 1000) {
        lastG = 0;
        return; // zweite Taste eines g-Chords
      }
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const active = document.activeElement;
      const inContainer = !!active && container.contains(active);
      if (!inContainer && active && active !== document.body) return;

      // Fokus auf der Zeile selbst (nicht auf einem Knopf darin): nur dann
      // gehören ↵/↑/↓ der Tabelle — sonst behält der Knopf sein ↵.
      const rowEl = active instanceof HTMLElement && active.hasAttribute("data-task-row") ? active : null;
      const action = rowActionForKey(e.key, rowEl !== null);
      if (!action) return;

      if (action.type === "move") {
        const rows = visibleRows(container);
        const cur = rowOf(active) ? rows.indexOf(rowOf(active)!) : -1;
        const next = rows[moveIndex(cur, rows.length, action.dir)];
        if (next) {
          e.preventDefault();
          next.focus();
          next.scrollIntoView?.({ block: "nearest" });
        }
        return;
      }
      const id = Number(rowEl!.dataset.taskRow);
      if (!Number.isFinite(id)) return;
      e.preventDefault();
      switch (action.type) {
        case "select":
          h.current.onSelect(id);
          break;
        case "open":
          h.current.onOpen(id);
          break;
        case "rename":
          h.current.onRename(id);
          break;
        case "status":
          h.current.onStatus(id);
          break;
        case "priority":
          h.current.onPriority(id);
          break;
        case "subtask":
          h.current.onSubtask(id);
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, containerRef]);
}
